import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getJudgmentMirror } from '../api/dailyLoop';
import { getDecisions } from '../api/decisions';
import { takeFirstPaint } from '../motion/columnMotion';
import '../styles/judgment.css';

const STAT_ORDER = ['held', 'holdTime', 'revisions', 'verdicts', 'counterEvidence'];
const VERDICT_LABEL = {
  held_up: 'held up',
  broke: 'broke',
  partly: 'partly held',
  unresolvable: 'could not be resolved',
  right_for_wrong_reasons: 'right for the wrong reasons'
};

const date = (value) => (
  value
    ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : ''
);

/* A row with nothing behind it is not shown. A dash is the software talking
   about itself. */
const hasData = (row) => {
  if (!row) return false;
  if (row.value && typeof row.value === 'object') return Object.values(row.value).some(count => Number(count) > 0);
  return row.value !== null && row.value !== undefined && Number(row.value) !== 0 && row.display !== '—';
};
const openStats = (stats = {}) => STAT_ORDER.map((key) => stats[key]).filter(hasData);

const bandLine = (band = {}) => {
  if (!band.sufficient) return band.silence || '';
  const low = band.range?.low == null ? '' : Math.round(band.range.low * 100);
  const high = band.range?.high == null ? '' : Math.round(band.range.high * 100);
  if (low === '' || high === '') return '';
  return `When you were ${band.confidence || 'this sure'}, later outcomes sat between ${low} and ${high} in a hundred.`;
};
const list = value => Array.isArray(value) ? value : [];
const safeHref = value => {
  const href = String(value || '').trim();
  return href.startsWith('/') && !href.startsWith('//') ? href : '';
};

const RetainedSources = ({ title, sources = [], empty }) => (
  <details className="judgment-mirror__sources">
    <summary>{title}{sources.length ? ` · ${sources.length}` : ''}</summary>
    {sources.length ? (
      <ul>
        {sources.map(source => (
          <li key={source.sourceRefId}>
            <strong>{source.title || 'Untitled retained source'}</strong>
            {source.snippet ? <blockquote>{source.snippet}</blockquote> : <p>Source text was not retained in this record.</p>}
            {source.attachedAt ? <time>Attached {date(source.attachedAt)}</time> : <small>Attachment date unknown</small>}
          </li>
        ))}
      </ul>
    ) : <p>{empty}</p>}
  </details>
);

const DecisionReplay = ({ item }) => {
  const [revealed, setRevealed] = useState(false);
  const decision = item?.decision || {};
  const basis = item?.basis;
  const outcome = item?.outcome || {};
  const reviewHref = safeHref(item?.subject?.href);
  return (
    <article className="judgment-mirror__decision">
      <header>
        <p>{decision.status || 'Recorded decision'} · {date(decision.decidedAt || decision.acceptedAt || decision.createdAt)}</p>
        <h3>{decision.summary || 'Untitled decision'}</h3>
      </header>
      {basis ? (
        <dl>
          <div><dt>View then</dt><dd>{basis.heldView || 'No held view was retained.'}</dd></div>
          <div><dt>Expected</dt><dd>{decision.expectedOutcome || 'No expectation was recorded.'}</dd></div>
          <div><dt>Test then</dt><dd>{basis.criterion || 'No reconsideration condition was recorded.'}</dd></div>
          <div><dt>Objection then</dt><dd>{basis.objection || 'No objection was recorded with this decision.'}</dd></div>
        </dl>
      ) : (
        <p className="judgment-mirror__silence">The retained decision-time basis could not be verified. Current case text is not substituted for it.</p>
      )}
      <RetainedSources
        title="What was attached then?"
        sources={list(basis?.attachedSources)}
        empty="No retained source excerpt was attached to this decision."
      />
      {!revealed ? (
        <button type="button" className="judgment-mirror__reveal" onClick={() => setRevealed(true)}>Show what happened</button>
      ) : (
        <div className="judgment-mirror__after" aria-live="polite">
          <p className="judgment-mirror__eyebrow">What happened</p>
          {outcome.state === 'observed' ? (
            <>
              <p><strong>{String(outcome.result || 'Observed').replaceAll('_', ' ')}</strong>{outcome.summary ? ` · ${outcome.summary}` : ''}</p>
              {outcome.calibrationNote ? <p>{outcome.calibrationNote}</p> : null}
            </>
          ) : (
            <p>No verified outcome has been recorded for this decision.</p>
          )}
          <RetainedSources
            title="What arrived afterward"
            sources={list(basis?.laterSources)}
            empty="No later case attachment is established in this record. This is not a claim that no later material exists."
          />
          {reviewHref ? <Link to={reviewHref}>Open the decision review</Link> : null}
          <small>Revealing this record changes no view, action, test, or assessment.</small>
        </div>
      )}
    </article>
  );
};

const JudgmentMirror = () => {
  const [params] = useSearchParams();
  const stat = params.get('stat') || '';
  const [mirror, setMirror] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [decisions, setDecisions] = useState(null);
  const [decisionError, setDecisionError] = useState('');
  const arriving = useMemo(() => takeFirstPaint(`judgment-mirror:${stat || 'home'}`), [stat]);

  useEffect(() => {
    document.body.classList.add('judgment-route');
    return () => document.body.classList.remove('judgment-route');
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const next = await getJudgmentMirror({ stat });
        if (!cancelled) setMirror(next);
      } catch (_loadError) {
        if (!cancelled) setError('The Mirror could not be read.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stat]);

  useEffect(() => {
    let cancelled = false;
    getDecisions({ filter: 'all', limit: 50, windowDays: 365 })
      .then(result => { if (!cancelled) setDecisions(result); })
      .catch(() => { if (!cancelled) setDecisionError('The decision record could not be read.'); });
    return () => { cancelled = true; };
  }, []);

  const step = (n) => (arriving ? `wfp-anim wfp-anim--${n}` : '');
  const ledger = mirror?.mirror || mirror || {};
  const stats = ledger.stats || mirror?.stats || {};
  const open = openStats(stats);
  const claims = Array.isArray(ledger.claims) ? ledger.claims : (Array.isArray(mirror?.claims) ? mirror.claims : []);
  const active = open.find((row) => row.id === stat) || null;
  const due = Array.isArray(ledger.due) ? ledger.due : [];
  const recorded = Array.isArray(ledger.verdicts) && !ledger.verdicts[0]?.href
    ? ledger.verdicts
    : [];
  const calibration = ledger.calibration || mirror?.calibration || null;

  return (
    <main className="judgment-mirror" aria-labelledby="judgment-mirror-title">
      <Link className={`judgment-back ${step(1)}`} to="/judgment">← Views</Link>
      <header className={step(1)}>
        <p>The casebook looking back</p>
        <h1 id="judgment-mirror-title">The Mirror</h1>
        <p>No score. Just the shape of how you change your mind. Every number is a door.</p>
      </header>

      {loading ? <p className="judgment-quiet" role="status">Letting the ink settle…</p> : null}
      {error ? <p className="judgment-error" role="alert">{error}</p> : null}

      {mirror ? (
        <div className={step(2)}>
          {open.length ? (
            <ul className="mirror__stats">
              {open.map((row) => (
                <li key={row.id} className={row.id === stat ? 'is-open' : ''}>
                  <Link to={row.href}>
                    <span className="mirror__stat-label">{row.label}</span>
                    <span className="mirror__stat-value">{row.display}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}

          {active ? (
            <section className="judgment-mirror__record" aria-labelledby="mirror-claims-title">
              <h2 id="mirror-claims-title">{active.label}</h2>
              {claims.length ? (
                <ul>
                  {claims.map((claim) => (
                    <li key={`${claim.pageId}:${claim.claimId || claim.verdictId || claim.href}`}>
                      <Link to={claim.href}>{claim.text}</Link>
                      {claim.verdict ? (
                        <span className="mirror__note">{String(claim.verdict).replace('_', ' ')}</span>
                      ) : null}
                      {claim.days != null ? (
                        <span className="mirror__note">{claim.days} days</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="judgment-mirror__silence">None yet.</p>
              )}
            </section>
          ) : null}

          {due.length ? (
            <section className="judgment-mirror__due">
              <h2>The date arrived</h2>
              {due.map((item) => (
                <Link key={item.pageId} to={`/judgment/${item.pageId}`}>
                  <span>{item.claim}</span>
                  <small>{date(item.horizonAt)}</small>
                </Link>
              ))}
            </section>
          ) : null}

          {decisionError || list(decisions?.items).length ? (
            <section id="decisions" className="judgment-mirror__decisions" aria-labelledby="judgment-mirror-decisions-title">
              <h2 id="judgment-mirror-decisions-title">Decisions</h2>
              {decisionError ? <p className="judgment-error" role="alert">{decisionError}</p> : null}
              {list(decisions?.items).length ? (
                <div className="judgment-mirror__decision-list">
                  {decisions.items.map(item => <DecisionReplay key={item.id} item={item} />)}
                </div>
              ) : null}
              {decisions?.nextCursor ? (
                <p className="judgment-mirror__silence">Showing the first 50 decisions. Open a case for the complete record.</p>
              ) : null}
            </section>
          ) : null}

          <section id="record" className="judgment-mirror__record">
            <h2>How views turned out</h2>
            {recorded.length ? recorded.map((verdict) => (
              <article key={verdict.verdictId || `${verdict.pageId}:${verdict.recordedAt}`}>
                <Link to={`/judgment/${verdict.pageId}`}>{verdict.claim}</Link>
                <p>{VERDICT_LABEL[verdict.result] || verdict.result}{verdict.note ? ` — ${verdict.note}` : ''}</p>
                <time>{date(verdict.recordedAt)}</time>
              </article>
            )) : (
              <p className="judgment-mirror__silence">No verdicts yet. The Mirror is allowed to be empty.</p>
            )}
          </section>

          {calibration?.private && calibration.overall?.sufficient && Array.isArray(calibration.byConfidence) ? (
            <section className="judgment-mirror__calibration" aria-labelledby="mirror-calibration-title">
              <h2 id="mirror-calibration-title">How certainty met the later world</h2>
              <p className="judgment-mirror__selection">{calibration.selection}</p>
              {calibration.byConfidence.map(band => <p key={band.confidence || 'band'}>{bandLine(band)}</p>)}
            </section>
          ) : null}
        </div>
      ) : null}
    </main>
  );
};

export default JudgmentMirror;
